import { useSelector } from "react-redux"
import { get } from "lodash"
import {
  useGetIlbaInclusionQuery,
  useAccountBalancesQuery,
  selectHasGsu,
  selectHasWss,
} from "@/store/apis/linkApi"
import { bodyContent } from "@/store/apis/matrixApi"
import { RootState } from "@/store"
import { IlbaResource } from "@/types/Ilba"
import {
  GSU_PRODUCT_ID,
  WSS_PRODUCT_ID,
  productIds,
} from "@/utils/helpers/mappings"
import { currentDate } from "@/utils/helpers/dateHelpers"
import {
  getOwnedIlbaProducts,
  getIlbaCampaignState,
  getPreferenceFormProducts,
  isPreferenceModuleTransferNoticeEligible,
  isExpiringWithinSixMonths,
  shouldShowIlbaPreferenceModule,
} from "@/utils/helpers/ilbaRules"

const ILBA_KEY_DATES_PATH = "csv_data.ilba-key-dates_csv"

const ILBA_PRODUCTS = [
  {
    code: "GSU",
    productId: GSU_PRODUCT_ID,
    name: productIds[GSU_PRODUCT_ID],
  },
  {
    code: "WSS",
    productId: WSS_PRODUCT_ID,
    name: productIds[WSS_PRODUCT_ID],
  },
] as const

// Helper to format API ISO dates into readable Australian dates (e.g. "20 Jul 2026")
function formatReadableDate(isoString: string) {
  return new Date(isoString).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

export function useIlbaEligibility(memberId: string) {
  const ilbaDates = useSelector((state: RootState) =>
    get(bodyContent(state), `${ILBA_KEY_DATES_PATH}.csvData`)
  )

  // =======================================================================
  // 1. API CALLS
  // =======================================================================
  const isValidMemberId =
    Boolean(memberId) &&
    memberId !== "undefined" &&
    memberId !== "null" &&
    memberId !== ""

  // Check which accounts the member actually owns first
  const { isFetching: isFetchingAccounts } = useAccountBalancesQuery(
    undefined,
    { skip: !isValidMemberId }
  )

  const hasGsu = useSelector(selectHasGsu)
  const hasWss = useSelector(selectHasWss)

  const {
    data: gsuData,
    isLoading: gsuLoading,
    error: gsuError,
  } = useGetIlbaInclusionQuery(
    { memberId, productId: String(GSU_PRODUCT_ID) },
    { skip: !isValidMemberId || isFetchingAccounts || !hasGsu }
  )

  const {
    data: wssData,
    isLoading: wssLoading,
    error: wssError,
  } = useGetIlbaInclusionQuery(
    { memberId, productId: String(WSS_PRODUCT_ID) },
    { skip: !isValidMemberId || isFetchingAccounts || !hasWss }
  )

  const isLoading =
    (isFetchingAccounts || gsuLoading || wssLoading) && isValidMemberId

  const ilbaProducts = [
    { ...ILBA_PRODUCTS[0], data: gsuData, error: gsuError },
    { ...ILBA_PRODUCTS[1], data: wssData, error: wssError },
  ] as const

  // =======================================================================
  // 2. GLOBAL CAMPAIGN STATE
  // =======================================================================
  const today = currentDate() // can test with query param 'date' e.g ?date=YYYYMMDD
  const campaignState = getIlbaCampaignState(today, ilbaDates)

  // =======================================================================
  // 3. INITIALISE OUTPUTS
  // =======================================================================
  let IlbaTransferData: null | {
    affectedProducts: { code: string; name: string; transferDate: string }[]
    unaffectedProducts: { code: string; name: string }[]
  } = null
  let IlbaExpiryData: null | {
    expiringProducts: { code: string; name: string; expiryDate: string }[]
  } = null
  let IlbaValidPreferenceData: null | {
    validExpiryProducts: { code: string; name: string; expiryDate: string }[]
  } = null
  let IlbaFormPreferenceData: null | {
    formProducts: { code: string; name: string }[]
  } = null
  let IlbaPreferenceNoticeData: null | {
    affectedProducts: { code: string; name: string; transferDate: string }[]
  } = null

  // =======================================================================
  // 4. INDEPENDENT FEATURE EVALUATIONS
  // =======================================================================
  const expiringProducts: { code: string; name: string; expiryDate: string }[] =
    []
  const validExpiryProducts: {
    code: string
    name: string
    expiryDate: string
  }[] = []
  const affectedProducts: {
    code: string
    name: string
    transferDate: string
  }[] = []
  const unaffectedProducts: { code: string; name: string }[] = []
  const preferenceNoticeProducts: {
    code: string
    name: string
    transferDate: string
  }[] = []

  const checkPreferenceExpiry = (
    code: string,
    name: string,
    ilba: IlbaResource
  ) => {
    if (ilba.electionFlag?.code === "Y" && ilba.expiryDate) {
      if (isExpiringWithinSixMonths(ilba.expiryDate, today)) {
        expiringProducts.push({
          code,
          name,
          expiryDate: formatReadableDate(ilba.expiryDate),
        })
      } else {
        const expiryDateValue = new Date(ilba.expiryDate)
        if (!isNaN(expiryDateValue.getTime()) && expiryDateValue > today) {
          validExpiryProducts.push({
            code,
            name,
            expiryDate: formatReadableDate(ilba.expiryDate),
          })
        }
      }
    }
  }

  const classifyTransferStatus = (
    code: string,
    name: string,
    ilba: IlbaResource
  ) => {
    // If they have an active preference ('Y'), they are protected, so NO alert.
    if (ilba.electionFlag?.code === "Y") {
      unaffectedProducts.push({ code, name })
      return
    }

    // If no preference protects them, check if MUFG flagged them for transfer
    if (ilba.criteria?.code === "Y") {
      affectedProducts.push({
        code,
        name,
        transferDate: ilba.transferDate
          ? formatReadableDate(ilba.transferDate)
          : "",
      })
    } else {
      unaffectedProducts.push({ code, name })
    }
  }

  // ---------------------------------------------------------------------
  // 4A. PREFERENCE EXPIRY (expiring & still-valid preferences)
  // ---------------------------------------------------------------------
  for (const { code, name, data } of ilbaProducts) {
    if (data) checkPreferenceExpiry(code, name, data)
  }

  if (expiringProducts.length > 0 && !isLoading) {
    IlbaExpiryData = { expiringProducts }
  }
  if (validExpiryProducts.length > 0)
    IlbaValidPreferenceData = { validExpiryProducts }

  // ---------------------------------------------------------------------
  // 4B. ATO TRANSFER CLASSIFICATION (homepage banners — unchanged)
  // ---------------------------------------------------------------------
  for (const { code, name, data } of ilbaProducts) {
    if (data) classifyTransferStatus(code, name, data)
  }

  // wait for all owned ILBA API calls before classifying — avoids single-account copy when both are affected
  if (
    affectedProducts.length > 0 &&
    campaignState.isWithinCampaign &&
    !isLoading
  ) {
    IlbaTransferData = { affectedProducts, unaffectedProducts }
  }

  // ---------------------------------------------------------------------
  // 4C. PREFERENCE MODULE ILBA NOTICE (criteria=Y, no expiry date)
  // ---------------------------------------------------------------------
  for (const { code, name, data } of ilbaProducts) {
    if (data && isPreferenceModuleTransferNoticeEligible(data)) {
      preferenceNoticeProducts.push({
        code,
        name,
        transferDate: data.transferDate
          ? formatReadableDate(data.transferDate)
          : "",
      })
    }
  }

  if (preferenceNoticeProducts.length > 0 && campaignState.isWithinCampaign) {
    IlbaPreferenceNoticeData = { affectedProducts: preferenceNoticeProducts }
  }

  // ---------------------------------------------------------------------
  // 5. PREFERENCE MODULE FORM ELIGIBILITY (owned GSU/WSS accounts)
  // ---------------------------------------------------------------------
  // preference module is shown for any member with GSU or WSS — no balance threshold per updated business rules
  const ownedProducts = getOwnedIlbaProducts({
    hasGsu,
    hasWss,
    products: ILBA_PRODUCTS,
  })

  const formProducts = getPreferenceFormProducts({
    ownedProducts,
    expiringProducts,
    transferNoticeProducts: preferenceNoticeProducts,
  })

  if (formProducts.length > 0) {
    IlbaFormPreferenceData = { formProducts }
  }

  // ---------------------------------------------------------------------
  // 6. PREFERENCE MODULE VISIBILITY
  // ---------------------------------------------------------------------
  const isPreferenceModuleVisible = shouldShowIlbaPreferenceModule({
    hasGsu,
    hasWss,
  })

  // =======================================================================
  // DEV & QA LOGGING (Hidden in production)
  // =======================================================================
  if (process.env.NODE_ENV !== "production") {
    if (!isLoading && isValidMemberId) {
      console.groupCollapsed(`🛠️ ILBA Hook Data | Member: ${memberId}`)
      ilbaProducts.forEach(({ code, data, error }, index) => {
        console.log(
          `${index + 1}. RAW API - ${code}:`,
          data,
          error ? { error } : ""
        )
      })
      console.log("2. CMS - ILBA key dates (raw):", ilbaDates)
      console.log("3. UI - Campaign State:", campaignState, {
        today: today.toISOString().slice(0, 10),
        hint: !ilbaDates?.length
          ? "No CSV data at csv_data.ilba-key-dates_csv"
          : !campaignState.isWithinCampaign
            ? "Outside campaign window — try ?date=20260715"
            : "Active campaign",
      })
      console.log("4. UI - Flagged for transfer Data:", IlbaTransferData)
      console.log("5. UI - Expiring preference Data:", IlbaExpiryData)
      console.log("6. UI - Valid Preference Data:", IlbaValidPreferenceData)
      console.log("7. UI - Preference Notice Data:", IlbaPreferenceNoticeData)
      console.log("8. UI - Form Preference Data:", IlbaFormPreferenceData)
      console.log(
        "9. UI - Preference Module Visible:",
        isPreferenceModuleVisible
      )
      console.groupEnd()
    }
  }

  // =======================================================================
  // 5. NEEDS HEADER ICON
  // =======================================================================
  // suppress header alert during ATO cut-off hide window (no election UI to deep-link to)
  const needsHeaderIcon =
    campaignState.isWithinCampaign &&
    !campaignState.isDuringBlackout &&
    (IlbaTransferData !== null || IlbaExpiryData !== null)

  // =======================================================================
  // 6. FINAL RETURN
  // =======================================================================

  return {
    isLoading,
    needsHeaderIcon,
    campaignState,
    IlbaTransferData,
    IlbaExpiryData,
    IlbaValidPreferenceData,
    IlbaFormPreferenceData,
    IlbaPreferenceNoticeData,
    isPreferenceModuleVisible,
  }
}
